import React, { useState } from 'react';
import { InquiryHero, SuccessBanner, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FileUploadField, ConditionalText, ConsentSection } from './FormFields';
import { TECH_CONSULT_OPTIONS, HOW_HEARD_OPTIONS, COUNTRY_LIST } from '../../data/partnerOptions';

const initial = {
  fullName: '', company: '', jobTitle: '', email: '', phone: '',
  countryIncorporation: '', countrySubsidiaries: [],
  consultationTopic: '', consultationTopicOther: '',
  industryFocus: '', industryFocusOther: '',
  scopeComplexity: '',
  format: '', timezone: '', timezoneOther: '', availability: '', language: '',
  projectDescription: '',
  howHeard: '', ndaRequired: '',
  consentRequired: false, consentOptional: false,
};

export const TechConsultationForm = () => {
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) { setShowConsentError(true); return; }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Contact Information', fields: [
          { label: 'Full name', value: data.fullName },
          { label: 'Company / organization', value: data.company },
          { label: 'Job title / role', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
          { label: 'Country of incorporation (Headquarters)', value: data.countryIncorporation },
        ]},
        { title: 'Consultation Scope', fields: [
          { label: 'Consultation topic', value: data.consultationTopic },
          { label: 'Consultation topic - specified', value: data.consultationTopicOther },
          { label: 'Industry focus', value: data.industryFocus },
          { label: 'Industry focus - specified', value: data.industryFocusOther },
          { label: 'Project scope & complexity', value: data.scopeComplexity },
        ]},
        { title: 'Scheduling & Preferences', fields: [
          { label: 'Preferred consultation format', value: data.format },
          { label: 'Your timezone', value: data.timezone },
          { label: 'Timezone - specified', value: data.timezoneOther },
          { label: 'Preferred availability', value: data.availability },
          { label: 'Preferred language', value: data.language },
        ]},
        { title: 'Background & Preparation', fields: [
          { label: 'Brief project description', value: data.projectDescription },
        ]},
        { title: 'Document Upload & Referral', fields: [
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
          { label: 'NDA required before consultation?', value: data.ndaRequired },
        ]},
        { title: 'Consent & Data Protection', fields: [
          { label: 'Required data-processing consent', value: data.consentRequired ? 'Yes - consent given' : 'No' },
          { label: 'Optional marketing consent', value: data.consentOptional ? 'Yes' : 'No' },
        ]},
      ];
      await submitInquiry({
        formType: 'technical-consultation', sections,
        meta: { fullName: data.fullName, companyName: data.company, businessEmail: data.email, country: '' },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const reset = () => { setData(initial); setFiles([]); setError(''); setSuccess(false); };

  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero
        title="Technical Consultation"
        subtitle="Expert advisory & project scoping"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <SuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Contact Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="fullName" label="Full name" required helper="Your first and last name." placeholder="e.g., Dr. Yuki Tanaka" value={data.fullName} onChange={set('fullName')} />
                  <TextField name="company" label="Company / organization" required helper="Your company or institution." placeholder="e.g., ABC Co., Ltd." value={data.company} onChange={set('company')} />
                  <TextField name="jobTitle" label="Job title / role" required helper="Your current position." placeholder="e.g., Head of Materials Science" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="We'll use this to schedule and confirm your consultation." placeholder="e.g., tanaka@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" helper="Include country code. Useful if you prefer a phone consultation." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                </div>
                <SelectField name="countryIncorporation" label="Country of incorporation (Headquarters)" required helper="Where is your company legally registered?" options={COUNTRY_LIST} value={data.countryIncorporation} onChange={set('countryIncorporation')} />
              </Section>

              <Section title="Consultation Scope">
                <SelectField name="consultationTopic" label="Consultation topic" required helper="Select the primary area where you need LAMKO's expertise." options={TECH_CONSULT_OPTIONS.consultationTopic} value={data.consultationTopic} onChange={set('consultationTopic')} />
                <ConditionalText show={data.consultationTopic === 'Other (please describe below)'} name="consultationTopicOther" placeholder="Please describe your consultation topic" value={data.consultationTopicOther} onChange={set('consultationTopicOther')} />
                <SelectField name="industryFocus" label="Industry focus" required helper="Primary industry context for this consultation." options={TECH_CONSULT_OPTIONS.industryFocus} value={data.industryFocus} onChange={set('industryFocus')} />
                <ConditionalText show={data.industryFocus === 'Other (please specify)'} name="industryFocusOther" placeholder="Please describe your industry focus" value={data.industryFocusOther} onChange={set('industryFocusOther')} />
                <SelectField name="scopeComplexity" label="Project scope & complexity" required helper="How extensive is the consulting engagement you anticipate?" options={TECH_CONSULT_OPTIONS.scopeComplexity} value={data.scopeComplexity} onChange={set('scopeComplexity')} />
              </Section>

              <Section title="Scheduling & Preferences">
                <SelectField name="format" label="Preferred consultation format" required helper="How would you like to conduct the consultation?" options={TECH_CONSULT_OPTIONS.format} value={data.format} onChange={set('format')} />
                <SelectField name="timezone" label="Your timezone" required helper="So we can propose convenient meeting times." options={TECH_CONSULT_OPTIONS.timezone} value={data.timezone} onChange={set('timezone')} />
                <ConditionalText show={data.timezone === 'Other (please specify in notes)'} name="timezoneOther" placeholder="Please specify your timezone" value={data.timezoneOther} onChange={set('timezoneOther')} />
                <SelectField name="availability" label="Preferred availability" required helper="When are you generally available?" options={TECH_CONSULT_OPTIONS.availability} value={data.availability} onChange={set('availability')} />
                <SelectField name="language" label="Preferred language" helper="Our team supports consultations in multiple languages." options={TECH_CONSULT_OPTIONS.language} value={data.language} onChange={set('language')} />
              </Section>

              <Section title="Background & Preparation">
                <TextareaField name="projectDescription" label="Brief project description" required helper="Describe the technical challenge or question you'd like to discuss. The more context you provide, the more productive our consultation will be." placeholder="e.g., We are exploring alternative host materials for blue phosphorescent OLEDs. Looking for LAMKO's perspective on structure-property relationships and potential custom synthesis of candidate molecules..." rows={6} value={data.projectDescription} onChange={set('projectDescription')} />
              </Section>

              <Section title="Document Upload">
                <FileUploadField name="files" label="Upload reference materials" helper="Share any relevant documents to help us prepare: project summaries, technical briefs, patent references (PDF, DOC, PPT - max 25 MB per file)." files={files} onChange={setFiles} />
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="This helps us understand our reach." options={HOW_HEARD_OPTIONS} value={data.howHeard} onChange={set('howHeard')} />
                <SelectField name="ndaRequired" label="NDA required before consultation?" helper="Let us know if confidentiality agreements are needed upfront." options={TECH_CONSULT_OPTIONS.ndaRequired} value={data.ndaRequired} onChange={set('ndaRequired')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="customer" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
